import { DataTexture, FloatType, NearestFilter, RGBAFormat } from 'three';
import type { PainterResidualWarp, ThinPlateField } from './painterWarp';

/*
 * The residual warp's splines reach the vertex shader as one 32-bit float texture read with
 * texelFetch, one texel a centre: its pixel divided by the spline scale, and its weight. As plain
 * uniform arrays each centre took two of the vertex stage's uniform vectors, which WebGL2 only
 * guarantees 256 of; a texture of this size needs one of its 16 vertex samplers. The floats are
 * those the uniforms carried, so the shader computes the same shift.
 */
export const WARP_FIELD_ROWS = { row: 0, column: 1 } as const;

export interface PainterWarpCounts {
  readonly row: number;
  /* Absent for a warp without a column shift, which then compiles without one. */
  readonly column?: number;
}

function fill(data: Float32Array, width: number, row: number, field: ThinPlateField): void {
  field.centresPx.forEach((centre, i) => {
    const at = (row * width + i) * 4;
    data[at] = centre[0] / field.scalePx;
    data[at + 1] = centre[1] / field.scalePx;
    data[at + 2] = field.weights[i]!;
  });
}

export function painterWarpTexture(warp: PainterResidualWarp): DataTexture {
  const columns = warp.vertical;
  const width = Math.max(1, warp.centresPx.length, columns?.centresPx.length ?? 0);
  const data = new Float32Array(width * 2 * 4);
  fill(data, width, WARP_FIELD_ROWS.row, warp);
  if (columns) fill(data, width, WARP_FIELD_ROWS.column, columns);
  const texture = new DataTexture(data, width, 2, RGBAFormat, FloatType);
  texture.minFilter = NearestFilter;
  texture.magFilter = NearestFilter;
  texture.generateMipmaps = false;
  texture.flipY = false;
  const counts: PainterWarpCounts = {
    row: warp.centresPx.length,
    ...(columns ? { column: columns.centresPx.length } : {}),
  };
  texture.userData['painterWarpCounts'] = counts;
  texture.needsUpdate = true;
  return texture;
}

/** The centre counts the texture was packed with, for the shaders' loop bounds. */
export function painterWarpCounts(texture: DataTexture): PainterWarpCounts {
  const counts = texture.userData['painterWarpCounts'] as PainterWarpCounts | undefined;
  if (!counts) throw new Error('The texture carries no residual warp');
  return counts;
}
